import { Controller } from '@hotwired/stimulus'
import { get, patch } from '../lib/request'
import { emailValid } from '../lib/validators/email'
import { on } from '../lib/events'

export default class extends Controller {
  static targets = [
    'country',
    'shipping',
    'name',
    'address1',
    'address2',
    'neighborhood',
    'zip',
    'state',
    'city',
    'phone',
    'email',
  ]

  static values = {
    url: String,
    valid: Boolean,
    orderUrl: String,
  }

  initialize = () => {
    this.generateForm()
  }

  connect = () => {
    on('addressFieldsLoaded', this.highlight)
  }

  generateForm = () => {
    const url = `${this.urlValue}?country_code=${this.countryTarget.value}`

    get(url, { responseKind: 'turbo-stream' })
  }

  highlight = () => {
    this.connect()
    this.highlightMissingFields()
    this.updateCheckout()
  }

  updateCheckout = (bypass = false) => {
    if (this.updateCheckoutCall) {
      clearTimeout(this.updateCheckoutCall)
    }

    this.updateCheckoutCall = setTimeout(() => {
      this._updateCheckout(bypass)
    }, 50)
  }

  _updateCheckout = (bypass = false) => {
    if (this.canUpdateCheckout || bypass) {
      patch(this.orderUrlValue, {
        body: this.checkoutParams,
        responseKind: 'turbo-stream',
      })
    }
  }

  get checkoutParams() {
    const completeParams = { ...this.baseParams, ...this.additionalParams }

    return { order: completeParams }
  }

  get baseParams() {
    return this.requiredFieldsArray.reduce((obj, val) => {
      obj[val] = this[`${val}Target`].value
      return obj
    }, {})
  }

  get additionalParams() {
    return {
      provider: 'stripe',
      country: this.countryTarget.value,
      ...(this.hasAddress2Target && { address2: this.address2Target.value }),
      ...(this.hasNameTarget && { first_name: this.nameTarget.value }),
      ...(this.hasPhoneTarget && { phone: this.phoneTarget.value }),
      ...(this.shippingValuePresent && { shipping_method: this.shippingTarget.value }),
    }
  }

  addressChanged = () => {
    this.highlightMissingFields()
    this.updateCheckout()
  }

  get canUpdateCheckout() {
    return this.addressFieldsValid && this.zipValid && this.emailValid
  }

  get shippingValuePresent() {
    return this.hasShippingTarget && this.shippingTarget.value.length > 0
  }

  get zipValid() {
    if (!this.hasZipTarget) {
      return true
    }
    else if (this.zipRegex) {
      return this.zipTarget.value && this.zipRegex.test(this.zipTarget.value)
    }
    else {
      return this.zipTarget.value
    }
  }

  get emailValid() {
    return this.emailTarget.value && emailValid(this.emailTarget.value)
  }

  highlightMissingFields = () => {
    if (this.addressFieldsHaveValues) {
      this.addressFields.forEach((ele) => {
        if (ele.value.length === 0) {
          ele.classList.add('border', 'border-red-500')
        }
        else {
          ele.classList.remove('border', 'border-red-500')
        }
      })

      this.fieldsWithRegex.forEach((val) => {
        const capVal = val[0].toUpperCase() + val.substring(1)

        if (!this[`has${capVal}Target`]) return

        if (this[`${val}Valid`]) {
          this[`${val}Target`].classList.remove('border', 'border-red-500')
        }
        else {
          this[`${val}Target`].classList.add('border', 'border-red-500')
        }
      })
    }
  }

  get fieldsWithRegex() {
    return ['zip', 'email']
  }

  get requiredFieldsArray() {
    return JSON.parse(this.nameTarget.dataset.requiredAddressFields)
  }

  get addressFields() {
    return this.requiredFieldsArray.map(ele => this[`${ele}Target`])
  }

  get addressFieldsValid() {
    return this.addressFields.every(field => field.value.length > 0)
  }

  get addressFieldsHaveValues() {
    return this.addressFields.some(field => field.value.length > 0)
  }

  get zipRegex() {
    const element = document.querySelector('[data-zip-regex]')

    if (element) {
      const str = element.dataset.zipRegex

      return new RegExp(str)
    }
    else {
      return null
    }
  }
}
